import Filter from './Filter';

/**
 * Base class for defining workflow board filters.
 *
 * Handles most filter management. Subclasses should only need to
 * override `matches`.
 */
class ItemFilter<T extends string | number> extends Filter<[string, T][]> {
  selectedItems: Set<T>;
  filterOptions: {};
  allOptions: Record<string, [string, T]> = {};

  /**
   * @param initialItems IDs of items initially selected by the filter
   * @param initialOptions [name, id] pairs for each item in initialItems
   * @param filterOptions filter options to customize the behavior of the filter
   */
  constructor(
    initialItems: T[],
    initialOptions: Array<[string, T]> = [],
    filterOptions: {} = {}
  ) {
    super();
    this.selectedItems = new Set(initialItems);
    this.setOptions(initialOptions);
    this.filterOptions = filterOptions;
  }

  /**
   * @param id
   */
  addItem(id: T) {
    this.selectedItems.add(id);
  }

  /**
   * @param id
   */
  deleteItem(id: T) {
    this.selectedItems.delete(id);
  }

  /**
   * @param items
   */
  set(items: T[]) {
    this.clearItems();
    items.forEach(item => this.addItem(item));
  }

  /**
   * @param items
   */
  setItems(items: T[]) {
    this.clearItems();
    items.forEach(item => this.addItem(item));
  }

  /**
   * @param id
   */
  itemIsSelected(id: T) {
    // @ts-expect-error not sure why ''
    return this.selectedItems.has(id || '');
  }

  clearItems() {
    this.selectedItems.clear();
  }

  single(): T {
    return [...this.selectedItems][0];
  }

  /**
   * Return the filter data to put in the bookmark config
   */
  toBookmarkConfiguration() {
    return Array.from(this.selectedItems);
  }

  addOption({ value, text }) {
    this.allOptions[value] = [text, value];
  }

  /**
   * Updates the set of all possible [name, id] pairs allowed by this
   * filter.
   */
  setOptions(options: Array<[string, T]>) {
    options.forEach(([text, value]) => {
      this.addOption({ value, text });
    });
  }

  /**
   * Returns all selected items in a form that can be passed into
   * Selectro.
   */
  asOptions() {
    return Array.from(this.selectedItems.keys())
      .map(item => this.allOptions[String(item)])
      .filter(f => f);
  }

  /**
   * Return all possible options in a form that can be passed into Selectro.
   */
  selectroOptions(): any {
    return Object.values(this.allOptions);
  }

  /**
   * Returns the value(s) in a format that can be passed into a form
   * input.
   */
  getValue() {
    return this.asOptions();
  }

  get hasItems() {
    return this.selectedItems.size > 0;
  }

  get hasValue() {
    return this.hasItems;
  }

  /**
   * Returns true if the record is matched by this filter, false
   * otherwise. The default is to match all records if no
   * selectedItems are defined.
   */
  matches(record: any) {
    return !this.hasItems;
  }
}

export default ItemFilter;
